import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import Instance from 'providers/instance';
import remoteIpInstance from 'providers/remote-ip';
import { userAgent } from 'providers/fingerprint';

import { putErrorResponse } from 'middleware/actions/error';
import { paymentEndpoint as endpoint } from 'providers/endpoints/payment';

import { formatKeys } from 'utils/formatter';
import { formatTransferConfirmation as formatTransactionResponse } from 'utils/transfer/formatter';

import {
  actions,
  putOtherBillFavouriteDetail,
  putOtherBillFavouriteConfirm,
  putOtherBillFavouritePayeeDetail,
  putOtherBillFavouriteTac,
  putOtherBillFavouriteResult,
} from 'middleware/actions/favourite/payment';
import { putOtherBillDetail, putOtherBillReceipt, putOtherBillTac } from 'middleware/actions/payment';
import { RECEIPT_KEYMAP } from 'settings/constants/keymap';

const getClientIP = () => remoteIpInstance.get();

const newFavouriteDetail = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getDetail, body);
const newFavouritePayeeDetail = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getPayeeList, body);
const newFavouriteDetailFromPayment = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getDetailFromPayment, body);
const newFavouriteConfirmation = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getConfirmation, body);
const newFavouriteTac = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getTac, body);
const newFavouriteResult = ({ body }) => Instance.post(endpoint.otherBill.favourite.add.getResult, body);
const getEditDetail = ({ body }) => Instance.post(endpoint.otherBill.favourite.edit.getDetail, body);
const getEditConfirmation = ({ body }) => Instance.post(endpoint.otherBill.favourite.edit.getConfirmation, body);
const getEditTac = ({ body }) => Instance.post(endpoint.otherBill.favourite.edit.getTac, body);
const getEditResult = ({ body }) => Instance.post(endpoint.otherBill.favourite.edit.getResult, body);
const getDeleteConfirm = ({ body }) => Instance.post(endpoint.otherBill.favourite.delete.getConfirmation, body);
const getDeleteTac = ({ body }) => Instance.post(endpoint.otherBill.favourite.delete.getTac, body);
const getDeleteResult = ({ body }) => Instance.post(endpoint.otherBill.favourite.delete.getResult, body);
const getPaymentDetail = ({ body }) => Instance.post(endpoint.otherBill.favourite.payment.getDetail, body);
const getPaymentTac = ({ body }) => Instance.post(endpoint.otherBill.favourite.payment.getTac, body);
const getPaymentReceipt = ({ body }) => Instance.post(endpoint.otherBill.favourite.payment.getReceipt, body);

const getLocale = state => state.LanguageReducer.locale;

const { CONFIRM: DELETE_CONFIRM, TAC: DELETE_TAC, RESULT: DELETE_RESULT } = actions.GET.OTHER_BILL.DELETE_FAVOURITE;
const { DETAIL: EDIT_DETAIL, CONFIRM: EDIT_CONFIRM, TAC: EDIT_TAC, RESULT: EDIT_RESULT } = actions.GET.OTHER_BILL.EDIT_FAVOURITE;
const {
  DETAIL: ADD_DETAIL,
  PAYEE_DETAIL: ADD_PAYEE_DETAIL,
  DETAIL_FROM_PAYMENT,
  CONFIRM: ADD_CONFIRM,
  TAC: ADD_TAC,
  RESULT: ADD_RESULT,
} = actions.GET.OTHER_BILL.ADD_FAVOURITE;
const { DETAIL: PAYMENT_DETAIL, TAC: PAYMENT_TAC, RECEIPT: PAYMENT_RECEIPT } = actions.GET.OTHER_BILL.PAYMENT_FROM_FAVOURITE;

function* getNewFavouriteOtherBillDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteDetail, { body });
    yield put(putOtherBillFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getNewFavouriteOtherBillDetailFromPaymentSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteDetailFromPayment, { body });
    yield put(putOtherBillFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getNewFavouriteOtherBillPayeeDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouritePayeeDetail, { body });
    yield put(putOtherBillFavouritePayeeDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getEditDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getEditDetail, { body });
    yield put(putOtherBillFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* otherBillConfirmationFavouriteSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteConfirmation, { body });
    yield put(putOtherBillFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getEditConfirmSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getEditConfirmation, { body });
    yield put(putOtherBillFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getDeleteConfirmSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getDeleteConfirm, { body });
    yield put(putOtherBillFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getCommonTacSaga({ payload, type }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);
    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const handler = {
      [ADD_TAC]: () => newFavouriteTac,
      [DELETE_TAC]: () => getDeleteTac,
      [EDIT_TAC]: () => getEditTac,
      [PAYMENT_TAC]: () => getPaymentTac,
      default: () => null,
    };

    const tacEndpoint = (handler[type] || handler.default)();

    const { data } = yield call(tacEndpoint, { body });
    yield put(putOtherBillFavouriteTac(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getCommonResultSaga({ payload, type }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);
    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const handler = {
      [ADD_RESULT]: () => newFavouriteResult,
      [DELETE_RESULT]: () => getDeleteResult,
      [EDIT_RESULT]: () => getEditResult,
      default: () => null,
    };

    const resultEndpoint = (handler[type] || handler.default)();

    const { data } = yield call(resultEndpoint, { body });
    yield put(putOtherBillFavouriteResult(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

// Payment flow from favourite list
function* getPaymentDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getPaymentDetail, { body });
    yield put(putOtherBillDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getPaymentTacSaga({ payload = {} }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);
    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const { data } = yield call(getPaymentTac, { body });
    yield put(putOtherBillTac(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getPaymentReceiptSaga({ payload = {} }) {
  try {
    const locale = yield select(getLocale);
    const initialBody = { locale, ...payload };
    const body = formatKeys(initialBody, RECEIPT_KEYMAP);
    const { data } = yield call(getPaymentReceipt, { body });

    yield put(putOtherBillReceipt(data));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

export default function* CreditCardFavourite() {
  yield all([
    takeLatest(ADD_DETAIL, getNewFavouriteOtherBillDetailSaga),
    takeLatest(DETAIL_FROM_PAYMENT, getNewFavouriteOtherBillDetailFromPaymentSaga),
    takeLatest(ADD_PAYEE_DETAIL, getNewFavouriteOtherBillPayeeDetailSaga),
    takeLatest(ADD_CONFIRM, otherBillConfirmationFavouriteSaga),
    takeLatest(ADD_TAC, getCommonTacSaga),
    takeLatest(ADD_RESULT, getCommonResultSaga),
    takeLatest(EDIT_DETAIL, getEditDetailSaga),
    takeLatest(EDIT_CONFIRM, getEditConfirmSaga),
    takeLatest(EDIT_TAC, getCommonTacSaga),
    takeLatest(EDIT_RESULT, getCommonResultSaga),
    takeLatest(DELETE_CONFIRM, getDeleteConfirmSaga),
    takeLatest(DELETE_TAC, getCommonTacSaga),
    takeLatest(DELETE_RESULT, getCommonResultSaga),
    takeLatest(PAYMENT_DETAIL, getPaymentDetailSaga),
    takeLatest(PAYMENT_TAC, getPaymentTacSaga),
    takeLatest(PAYMENT_RECEIPT, getPaymentReceiptSaga),
  ]);
}
